import { Bot, CalendarClock, HelpCircle, Terminal, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Avatar, AvatarFallback, AvatarImage } from '~/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { useActorResolver } from '~/hooks/use-actor-resolver';
import type { ActorKind, ActorRef } from '~/types/actor.types';
import { cn } from '~/utils/cn';

/** Non-human actor kinds get an icon tile instead of an avatar/initials. */
const KIND_ICON: Partial<Record<ActorKind, LucideIcon>> = {
  SCHEDULE: CalendarClock,
  SERVICE: Bot,
  SYSTEM: Terminal,
  UNKNOWN: HelpCircle,
};

/**
 * Initials for the avatar fallback, derived from `displayName` because that is
 * the only name the envelope guarantees. Capped at two characters, and skipped
 * entirely for a short-id label ("User 8f3a…9c21"), where "U9" would read as a
 * real person's initials.
 */
function actorInitials(displayName: string, resolved: boolean): string | null {
  if (!resolved) return null;
  const [first, second] = displayName.trim().split(/\s+/).filter(Boolean);
  if (!first) return null;
  const picked = second ? `${first.slice(0, 1)}${second.slice(0, 1)}` : first.slice(0, 2);
  return picked.toUpperCase();
}

interface ActorLabelProps {
  /** The envelope. `null`/`undefined` renders `fallback` — an absent actor is
   *  not the same as an unresolved one. */
  actor: ActorRef | null | undefined;
  /** Show the leading avatar / icon tile. Turn off for dense table cells. */
  showAvatar?: boolean;
  /** Rendered when there is no actor at all. Defaults to a muted em-dash. */
  fallback?: ReactNode;
  /** Show the actor's email under the name (detail panels, not table cells). */
  showEmail?: boolean;
  className?: string;
  /** `data-testid` root (default `actor-label`); derives `-empty`, `-avatar`, `-name`, `-email`, `-tooltip`. */
  testId?: string;
}

/**
 * The single renderer for the API's actor envelope — use it everywhere a
 * "who did this" field is displayed rather than reading `displayName` directly,
 * so machine actors, departed users and legacy rows all degrade consistently.
 *
 * Presentation lives here on purpose: the backend deliberately does not
 * title-case an email local-part (`vimal.ads@lily.ai` stays `"vimal.ads"`,
 * because `"Vimal Ads"` would be wrong), so any casing or truncation decision
 * is the UI's to make.
 */
export function ActorLabel({
  actor: rawActor,
  showAvatar = true,
  fallback,
  showEmail = false,
  className,
  testId = 'actor-label',
}: ActorLabelProps) {
  const { t } = useTranslation('common');
  // STOPGAP: upgrades a server-unresolved actor to a real name from the
  // subsidiary's member list. Applied here rather than at each call site so
  // every actor on every page benefits from one change — and so it is one
  // import to delete once the API resolves names reliably. See
  // `use-actor-resolver.ts` for why it exists and what it cannot fix.
  const resolveActor = useActorResolver();
  const actor = resolveActor(rawActor);

  if (!actor) {
    return (
      <span className={cn('text-muted-foreground', className)} data-testid={`${testId}-empty`}>
        {fallback ?? t('actor.none')}
      </span>
    );
  }

  const Icon = KIND_ICON[actor.kind];
  const initials = actorInitials(actor.displayName, actor.resolved);
  const tooltipKey = actorTooltipKey(actor);
  const tooltip = tooltipKey ? t(tooltipKey, { name: actor.displayName }) : actor.email;

  const label = (
    <span
      className={cn('flex min-w-0 items-center gap-2', className)}
      data-testid={testId}
      // Radix's Tooltip.Trigger defaults to a button but adds no tabIndex when
      // `asChild` hands it a span, so without this the tooltip is mouse-only —
      // and for an unresolved actor that tooltip carries the only explanation of
      // why the label is a short id. Only focusable when there is one to show.
      {...(tooltip ? { tabIndex: 0 } : {})}
    >
      {showAvatar && (
        <Avatar className="h-6 w-6 shrink-0" data-testid={`${testId}-avatar`}>
          {actor.avatarUrl && <AvatarImage src={actor.avatarUrl} alt={actor.displayName} />}
          <AvatarFallback className="text-[10px] font-medium">
            {Icon ? <Icon className="h-3 w-3" /> : (initials ?? <HelpCircle className="h-3 w-3" />)}
          </AvatarFallback>
        </Avatar>
      )}
      <span className="flex min-w-0 flex-col">
        <span
          className={cn(
            // Font size is inherited, never set here: this renders inside
            // `text-xs` meta lines and table cells as well as `text-sm` detail
            // rows, and hardcoding a size made the actor larger than every
            // label beside it. Callers pass the size via `className`.
            'truncate',
            // Unresolved labels are short ids, not names — muting them keeps a
            // column from reading as though half its rows have real identities.
            actor.resolved ? 'text-foreground' : 'text-muted-foreground italic',
          )}
          data-testid={`${testId}-name`}
        >
          {actor.displayName}
        </span>
        {/* `showEmail` assumes a `text-sm` or larger context, so the email reads
            as secondary to the name above it. */}
        {showEmail && actor.email && (
          <span className="text-muted-foreground truncate text-xs" data-testid={`${testId}-email`}>
            {actor.email}
          </span>
        )}
      </span>
    </span>
  );

  if (!tooltip) return label;

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>{label}</TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs" data-testid={`${testId}-tooltip`}>
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Which tooltip explains this actor — a translation key, or the email verbatim
 * for a plainly resolved human. Returns the key rather than the rendered string
 * so this stays pure and the `t` overloads don't have to be re-typed here.
 */
function actorTooltipKey(actor: ActorRef): string | null {
  // The short-id case matters most: without an explanation the user cannot tell
  // a truncated id from a broken field.
  if (!actor.resolved) return 'actor.unresolvedTooltip';
  switch (actor.kind) {
    case 'SCHEDULE':
      return 'actor.scheduleTooltip';
    case 'SERVICE':
      return 'actor.serviceTooltip';
    case 'SYSTEM':
      return 'actor.systemTooltip';
    default:
      return null;
  }
}
